import { faEye, faInfo, faTriangleExclamation, faUtensils } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import moment from "moment-timezone";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row, Tooltip } from "reactstrap";
import useSWR from "swr";
import Button from "../../../../../components/Button";
import Modal from "../../../../../components/Modal";
import useApi from "../../../../../utils/api";
import { useAccess } from "../../../../../utils/hooks/access";
import { useCompanyDateFormat } from "../../../../../utils/hooks/company";
import { useVisible } from "../../../../../utils/hooks/useVisible";
import { getSharedDateRowSpan, isNonProductiveTimeSheet } from "../helpers";

const getSuffix = (number) => {
  const suffix = ["th", "st", "nd", "rd"];
  const value = number % 100;
  return suffix[(value - 20) % 10] || suffix[value] || suffix[0];
}

const ErrorTooltip = ({ row, errors }) => {
  const error = useMemo(() => {
    if (!errors[row.id]) {
      return false;
    }
    return Object.values(errors[row.id]).find((err) => !!err)
  }, [errors, row.id]);

  const { visible, toggle } = useVisible();

  if (!error) {
    return null;
  }

  const id = `timesheet-${row.original.user.id}-row-${row.id}-error`;

  return (
    <>
      <span id={id}>
        <FontAwesomeIcon
          className="cursor-pointer text-red"
          icon={faTriangleExclamation}
          size="lg"
        />
        <Tooltip target={id} isOpen={visible} toggle={toggle}>
          <span className="text-sm font-weight-bolder px-2 m-2">
            {error}
          </span>
        </Tooltip>
      </span>
    </>
  );
}

const WaiveTooltip = ({ data, waive, isMyTimesheet, timesheets }) => {
  const { t } = useTranslation();
  const { visible: tooltipVisible, toggle: toggleTooltip } = useVisible();
  const id = `meal-waive-${data.user.id}-row-${data.id}`;
  const {
    visible: confirmationModalVisible,
    open: openConfirmationModal,
    close: closeConfirmationModal,
  } = useVisible();

  const { authGet } = useApi();
  const {
    data: mealPremiumRule,
    isLoading: isLoadingMealPremiumRule,
  } = useSWR(
    () => {
      if (!data?.calculationGroup?.mealPremium?.id) {
        return null;
      }
      return `/calc-rules/meal-premium/${data.calculationGroup.mealPremium.id}`;
    },
    (url) => {
      return authGet(url);
    },
  );

  const mealPremiums = timesheets.filter((item) => {
    return item.appliedRules?.includes("mealPremium");
  });
  const waivedPremium = mealPremiums.find((item) => {
    return !!item.mealWaiveStatus;
  });

  const { hasAccess: canCreateMealWaive } = useAccess("requests.timesheet.mealPremiumWaive.canCreate");
  const { hasAccess: canApproveMealWaive } = useAccess("requests.timesheet.mealPremiumWaive.team.canApprove");
  const canWaive = (
    (
      isMyTimesheet
      && mealPremiumRule?.canWaive
      && !waivedPremium
      && canCreateMealWaive
    )
    || (
      !isMyTimesheet
      && mealPremiumRule?.canManagerWaive
      && !waivedPremium
      && canApproveMealWaive
    )
  );

  const content = useMemo(() => {
    if (isLoadingMealPremiumRule) {
      return (
        <p>
          loading meal premium rule
        </p>
      );
    }

    if (!mealPremiumRule) {
      return (
        <p>
          could not find meal premium rule
        </p>
      );
    }

    if (isMyTimesheet) {
      return (
        <p>
          {t("i-agree-to-waive-my-meal-periods-as-follows")}:

          <ul>
            {mealPremiumRule.rules.map((rule, i) => {
              const nextRule = mealPremiumRule.rules[i + 1];
              const nextRuelDuration = nextRule
                ? moment.duration(nextRule.duration, 'm').subtract(1, 'm').asHours()
                : null;
              return (
                <li key={i.toString()}>
                  <div>
                    {nextRuelDuration
                      ? t("meal-premium-agreement-1-2", {
                        min: rule.amount,
                        max: moment.duration(rule.duration, 'm').subtract(1, 'm').asHours(),
                        amount: nextRuelDuration
                      })
                      : t("meal-premium-agreement-1-1", {
                        min: rule.amount,
                        max: moment.duration(rule.duration, 'm').subtract(1, 'm').asHours(),
                      })}
                  </div>
                  <div>
                    {nextRuelDuration ? t("meal-premium-agreement-2-2", { amount: nextRuelDuration }) : t("meal-premium-agreement-2-1")}
                  </div>
                </li>
              );
            })}
          </ul>

          {mealPremiums.length === 1 && (
            t("meal-premium-agreement-3-1")
          )}
          {mealPremiums.length === 2 && (
            t("meal-premium-agreement-3-2")
          )}
          {mealPremiums.length > 2 && (
            t("meal-premium-agreement-3-3", {
              waive: `${mealPremiums.length}${getSuffix(mealPremiums.length)}`,
              take: `${mealPremiums.slice(2).map((_, i) => `${i + 1}${getSuffix(i + 1)}`).join(', ')} and ${mealPremiums.length - 1}${getSuffix(mealPremiums.length - 1)}`,
            })
          )}
          {" "}
          {t("meal-premium-agreement-4")}
        </p>
      )
    } else {
      return (
        <p>
          {t("manager-meal-premium-agreement-1")}
          <br />
          {t("manager-meal-premium-agreement-2")}
        </p>
      );
    }
  }, [t, isMyTimesheet, isLoadingMealPremiumRule, mealPremiumRule, mealPremiums]);

  if (
    !data.mealWaiveStatus
    && !canWaive
  ) {
    return null;
  }

  // TODO: depending on the user who waived the premium, the approved tooltip should show either that the request was approved or that the manager waived the premium itself

  return (
    <span id={id}>
      <FontAwesomeIcon
        className={classNames(
          (!waivedPremium && isMyTimesheet) && "text-primary",
          !waivedPremium && "cursor-pointer",
          waivedPremium?.mealWaiveStatus === "approved" && "text-info"
        )}
        icon={faUtensils}
        size="lg"
        onClick={
          canWaive
            ? () => openConfirmationModal()
            : undefined
        }
      />

      <Tooltip target={id} isOpen={tooltipVisible} toggle={toggleTooltip}>
        <span className="text-sm font-weight-bolder px-2 m-2">
          {!waivedPremium?.mealWaiveStatus && canWaive && t("click-to-waive-meal-premium")}
          {!waivedPremium?.mealWaiveStatus && !canWaive && t("meal-premium-not-available-for-waive")}
          {waivedPremium?.mealWaiveStatus === "pending" && t("meal-waive-request-info")}
          {waivedPremium?.mealWaiveStatus === "approved" && t("meal-waive-request-approved")}
        </span>
      </Tooltip>

      <Modal
        size="lg"
        title={
          <div className="d-flex justify-content-between align-items-center text-primary">
            {t("meal-break-waiver-agreement")}
            <i
              className="fa-solid fa-close cursor-pointer"
              onClick={closeConfirmationModal}
            />
          </div>
        }
        toggle={closeConfirmationModal}
        isOpen={confirmationModalVisible}
        scrollable
        centered
      >
        {content}

        <Row>
          <Col md="6">
            <Button color="danger" className="w-100" onClick={() => closeConfirmationModal()}>
              {t("close")}
            </Button>
          </Col>

          <Col md="6">
            <Button color="primary" className="w-100" onClick={() => waive(data.date)} disabled={!canWaive}>
              {t("confirm")}
            </Button>
          </Col>
        </Row>
      </Modal>
    </span>
  )
}

export const useDateColumn = ({ dateTimesheetMap, view, viewSummary, waive, isMyTimesheet }) => {
  const { t } = useTranslation();
  const dateFormat = useCompanyDateFormat();

  return useMemo(() => ({
    Header: t("date"),
    accessor: "date",
    headCustomProps: { className: "input-column" },
    getCellCustomProps: (timeSheet) => ({
      rowSpan: getSharedDateRowSpan(dateTimesheetMap, timeSheet),
    }),
    Cell: ({ value, row, errors, data }) => {
      const { isAdded } = row.original;
      const date = moment(row.original.date)
        .startOf("day")
        .toISOString(true);
      const timesheets = data?.filter((item) => moment(item.date).format("YYYY-MM-DD") === moment(date).format("YYYY-MM-DD"));
      const nonProductiveTimeSheets = timesheets.filter((timesheet) => isNonProductiveTimeSheet(timesheet));
      const manuallyUpdated = dateTimesheetMap?.[moment(date).format("YYYY-MM-DD")]?.find(
        ({ manuallyUpdated }) => manuallyUpdated);
      const mealWaiveStatus = row?.original?.mealWaiveStatus;

      return (
        <div className="d-flex gap-x-2 align-items-center date-column">
          {(
            !(isNonProductiveTimeSheet(row.original) && timesheets.length > 1) ||
            (isNonProductiveTimeSheet(row.original) && (nonProductiveTimeSheets.length === timesheets.length))
          ) ? (
            <>
              <span>{moment(value, "YYYY-MM-DD").format(dateFormat)}</span>
              <span style={{ width: '32px' }}>{moment(value, "YYYY-MM-DD").format(`ddd`).toUpperCase()}</span>
            </>
          ) : <div style={{ width: "107px" }} />}

          {(
            (view && !isNonProductiveTimeSheet(row.original)) ||
            (view && isNonProductiveTimeSheet(row.original) && nonProductiveTimeSheets.length === timesheets.length)
          ) && (
              <span
                className="cursor-pointer"
                onClick={() => view(row.original)}
              >
                <FontAwesomeIcon
                  icon={faInfo}
                  size="lg"
                  className={classNames(manuallyUpdated && "text-red")}
                />
              </span>
            )}

          {(
            !isAdded &&
            (row.original.appliedRules || []).includes("mealPremium")
          ) && (
              <WaiveTooltip
                data={row.original}
                waive={waive}
                isMyTimesheet={isMyTimesheet}
                timesheets={timesheets}
              />
            )}

          {(
            (!isAdded && viewSummary && !isNonProductiveTimeSheet(row.original)) ||
            (!isAdded && viewSummary && isNonProductiveTimeSheet(row.original) && nonProductiveTimeSheets.length === timesheets.length)
          ) && (
              <span
                className="cursor-pointer"
                onClick={() => viewSummary(date)}
              >
                <FontAwesomeIcon icon={faEye} size="lg" />
              </span>
            )}

          <ErrorTooltip row={row} errors={errors} />
        </div>
      );
    },
    alwaysVisible: true,
  }), [t, dateTimesheetMap, dateFormat, view, viewSummary, waive, isMyTimesheet]);
};
